/**
 * Application Router
 * Main routing configuration with React Router v7
 */

import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Loading } from '@/shared/components/feedback';
import { ProtectedRoute } from './protected-route';
import { PublicRoute } from './public-route';
import { ROUTES } from '@/shared/constants';

// Lazy-loaded pages
const HomePage = lazy(() => import('@/features/home/ui/pages/home-page'));
const CaseStudyDetailPage = lazy(() => import('@/features/home/ui/pages/case-study-detail-page'));
const LoginPage = lazy(() => import('@/features/auth/ui/pages/login-page'));
const RegisterPage = lazy(() => import('@/features/auth/ui/pages/register-page'));
const DashboardPage = lazy(() => import('@/features/dashboard/ui/pages/dashboard-page'));
const ProfilePage = lazy(() => import('@/features/profile/ui/pages/profile-page'));
const SettingsPage = lazy(() => import('@/features/settings/ui/pages/settings-page'));
const NotFoundPage = lazy(() => import('@/features/error/ui/pages/not-found-page'));

/**
 * Application routes configuration
 */
const router = createBrowserRouter([
  {
    path: ROUTES.HOME,
    element: (
      <Suspense fallback={<Loading />}>
        <HomePage />
      </Suspense>
    ),
  },
  {
    path: '/case-study/:slug',
    element: (
      <Suspense fallback={<Loading />}>
        <CaseStudyDetailPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.LOGIN,
    element: (
      <PublicRoute>
        <Suspense fallback={<Loading />}>
          <LoginPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.REGISTER,
    element: (
      <PublicRoute>
        <Suspense fallback={<Loading />}>
          <RegisterPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.DASHBOARD,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<Loading />}>
          <DashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.PROFILE,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<Loading />}>
          <ProfilePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.SETTINGS,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<Loading />}>
          <SettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<Loading />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

/**
 * Router component
 */
export function AppRouter() {
  return <RouterProvider router={router} />;
}
